import { Dispatch, SetStateAction } from 'react';
import { useSelector } from 'react-redux';
import { FlexContainer, Icon, Inputs, Text } from '@taxdown/piccolo-web';
import { locizeNS } from 'Services/i18n';
import { routeStateSelector } from 'Store/modules/router/selectors';
import { useTranslation } from 'Hooks/useTranslation';
import { TAXDOWN_ASSETS_DOMAIN } from 'Constants/domains';
import { FRONT_PLANS, PLANS_MODE } from 'Constants/plans';
import { PlansType } from 'Types/common';
import { increasePrice, parsePriceFormat } from '../PaymentLater/utils';
import * as Styled from './styles';

interface UpsellingProInfoProps {
  planType: string;
  setPlanType: Dispatch<SetStateAction<FRONT_PLANS>>;
  planPro: PlansType;
  planFull: PlansType;
}

export const UpsellingProInfo = ({
  planType,
  planPro,
  setPlanType,
  planFull,
}: UpsellingProInfoProps): JSX.Element => {
  const { t } = useTranslation(locizeNS.RESULT);
  const paymentLaterProps = useSelector(routeStateSelector);
  const userPlan = planType === FRONT_PLANS.FULL ? planFull : planPro;

  return (
    <Styled.InfoContent direction="column" align="center" gap="dimension_16">
      <FlexContainer align="center" gap="dimension_04">
        <img
          src={`${TAXDOWN_ASSETS_DOMAIN}/plans/smart-filter/v2/${PLANS_MODE[planType]}_logo.svg`}
          alt="live_logo"
        />
        <Text type="headline" size="S_L_M" color="dark">
          {planType}
        </Text>
      </FlexContainer>
      <Text type="paragraphs" size="M_M_S" color="dark" align="center">
        {t(`UpsellingPro.${planType}.description`)}
      </Text>
      <Text type="paragraphs" size="M_XL_S" color="dark" align="center">
        {t(`UpsellingPro.${planType}.info`)}
      </Text>
      <FlexContainer direction="column" gap="dimension_08" fullWidth>
        <Styled.BulletContainer>
          <Icon icon="TickXS" color="active" />
          <Text type="paragraphs" color="grey" size="S_M_S">
            {t(`UpsellingPro.${planType}.Bullet.1`, {
              context: paymentLaterProps?.fromFedUser && 'fed',
            })}
          </Text>
        </Styled.BulletContainer>
        <Styled.BulletContainer>
          <Icon icon="TickXS" color="active" />
          <Text type="paragraphs" color="grey" size="S_M_S">
            {t(`UpsellingPro.${planType}.Bullet.2`, {
              context: paymentLaterProps?.fromFedUser && 'fed',
            })}
          </Text>
        </Styled.BulletContainer>
        <Styled.BulletContainer>
          <Icon icon="TickXS" color="active" />
          <Text type="paragraphs" color="grey" size="S_M_S">
            {t(`UpsellingPro.${planType}.Bullet.3`, {
              context: paymentLaterProps?.fromFedUser && 'fed',
            })}
          </Text>
        </Styled.BulletContainer>
      </FlexContainer>
      <Styled.PriceContainer direction="column" fullWidth gap="dimension_16">
        <FlexContainer justify="space-between" align="center">
          <Text type="paragraphs" size="S_L_S" color="dark" align="center">
            {t(`UpsellingPro.${planType}.plan`)}
          </Text>
          <FlexContainer gap="dimension_04">
            <Styled.Price type="paragraphs" size="M_S_S" color="danger_strong" align="center">
              {increasePrice(Number(parsePriceFormat(userPlan?.priceUser?.amount ?? 0, true)))}
            </Styled.Price>
            <Text type="paragraphs" size="M_XL_S" color="dark" align="center">
              {parsePriceFormat(userPlan?.priceUser?.amount ?? 0)}
            </Text>
          </FlexContainer>
        </FlexContainer>
        <FlexContainer justify="space-between" align="center" fullWidth>
          <Text type="paragraphs" size="XS_L_S" color="grey" align="center">
            {t(`UpsellingPro.${planType}.switch`)}
          </Text>
          <Inputs.Switcher
            size="S"
            value={planType === FRONT_PLANS.FULL}
            onChange={() =>
              setPlanType(planType === FRONT_PLANS.FULL ? FRONT_PLANS.PRO : FRONT_PLANS.FULL)
            }
          />
        </FlexContainer>
      </Styled.PriceContainer>
    </Styled.InfoContent>
  );
};
